import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { UserGroupIcon, PencilIcon } from '@heroicons/react/24/outline';

interface ProjectType {
  id: number;
  title: string;
  description: string;
  team_members: string[] | string;
  nominatedCategories?: string[];
}

interface ProjectCardProps {
  project: ProjectType;
  onNominate: (projectId: number, category: string) => Promise<void>;
  onRemoveNomination?: (projectId: number, category: string) => Promise<void>;
  onVote?: (projectId: number, category: string) => void;
  isVotingPhase: boolean;
  selectedCategory?: string | boolean;
  labDayStatus: string;
  slug: string;
  isWinner?: {
    Innovation: boolean;
    Execution: boolean;
  };
  userVotes?: Record<string, number>;
  userNominations?: Array<{project_id: number, category: string}>;
  userProjects?: number[]; // Array of project IDs created by user
  onProjectUpdate?: () => void; // Callback after project update
}

type VoteCategory = 'Innovation' | 'Execution';
type VoteCounts = Record<VoteCategory, number>;

const ProjectCard: React.FC<ProjectCardProps> = ({ 
  project, 
  onNominate, 
  onRemoveNomination,
  onVote = () => {}, 
  isVotingPhase, 
  selectedCategory,
  labDayStatus,
  slug,
  isWinner = { Innovation: false, Execution: false },
  userVotes,
  userNominations = [],
  userProjects = [],
  onProjectUpdate
}) => {
  const [voteCounts, setVoteCounts] = useState<VoteCounts>({
    Innovation: 0,
    Execution: 0
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: project.title,
    description: project.description,
    team_members: Array.isArray(project.team_members) 
      ? project.team_members.join(', ') 
      : project.team_members
  });

  const isUserProject = userProjects.includes(project.id);

  useEffect(() => {
    // Only fetch vote counts if voting has ended
    if (labDayStatus === 'closed') {
      const fetchVoteCounts = async () => {
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          const response = await axios.get(`${apiUrl}/api/lab-days/${slug}/projects/${project.id}/votes`);
          setVoteCounts({
            Innovation: response.data.Innovation || 0,
            Execution: response.data.Execution || 0
          });
        } catch (error) {
          console.error('Error fetching vote counts:', error);
        }
      };
      fetchVoteCounts();
    }
  }, [project.id, labDayStatus, slug]);

  useEffect(() => {
    // Update edit form when project changes
    setEditForm({
      title: project.title,
      description: project.description,
      team_members: Array.isArray(project.team_members) 
        ? project.team_members.join(', ') 
        : project.team_members
    });
  }, [project]);

  // Handle both string and array formats for team members
  const teamMembers = Array.isArray(project.team_members) 
    ? project.team_members 
    : project.team_members.split(',').map(member => member.trim());

  const handleVote = async (category: VoteCategory) => {
    if (onVote) {
      onVote(project.id, category);
    }
  };

  const renderNominateButton = (category: VoteCategory) => {
    const alreadyNominated = project.nominatedCategories?.includes(category);
    const userNominatedThisProject = userNominations.some(
      nom => nom.project_id === project.id && nom.category === category
    );

    if (labDayStatus === 'submission') {
      // If user nominated this project in this category, show remove button
      if (userNominatedThisProject && onRemoveNomination) {
        return (
          <button
            onClick={() => onRemoveNomination(project.id, category)}
            className="py-2 px-4 rounded-lg text-sm font-medium transition-all bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-300 relative group"
            title="Click to remove your nomination"
          >
            {category}
            <span className="ml-2 text-sm group-hover:text-blue-600">×</span>
          </button>
        );
      }
      // If project is nominated but not by this user
      else if (alreadyNominated && !userNominatedThisProject) {
        return (
          <button
            disabled
            className="py-2 px-4 rounded-lg text-sm font-medium bg-green-200 text-green-800 cursor-not-allowed border border-green-400"
          >
            {category} ✔️
          </button>
        );
      }
      // If not nominated by anyone yet, show nominate button
      else if (!alreadyNominated) {
        return (
          <button
            onClick={() => onNominate(project.id, category)}
            className="py-2 px-4 rounded-lg text-sm font-medium transition-all bg-shopware-light-blue text-shopware-dark-blue hover:bg-shopware-blue hover:text-white"
          >
            {category}
          </button>
        );
      }
    }
    return null;
  };

  const renderVoteButton = (category: VoteCategory) => {
    const isNominated = project.nominatedCategories?.includes(category);
    const alreadyVoted = userVotes && userVotes[category] === project.id;
    if (labDayStatus === 'voting' && isNominated) {
      return (
        <button
          onClick={() => onVote && onVote(project.id, category)}
          disabled={alreadyVoted}
          className={`py-2 px-4 rounded-xl text-sm font-medium transition-all
            ${alreadyVoted
              ? 'bg-green-200 text-green-800 cursor-not-allowed border border-green-400'
              : 'bg-shopware-blue text-white hover:bg-shopware-dark-blue'}
          `}
        >
          {alreadyVoted ? `${category} gewählt ✔️` : `Für ${category} abstimmen`}
        </button>
      );
    } else if (labDayStatus === 'closed') {
      return (
        <div className={`text-shopware-dark-blue ${isWinner[category] ? 'font-bold' : ''}`}>
          {category}: {voteCounts[category]} votes
          {isWinner[category] && (
            <span className="ml-2 text-green-600">🏆 Winner!</span>
          )}
        </div>
      );
    }
    return null;
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.put(`${apiUrl}/api/lab-days/${slug}/projects/${project.id}`, editForm);
      toast.success('Project updated successfully!');
      setShowEditModal(false);
      if (onProjectUpdate) {
        onProjectUpdate();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update project';
      toast.error(errorMessage);
      console.error('Error updating project:', error);
    }
  };

  return (
    <>
      <div className={`bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg ${
        (isWinner.Innovation || isWinner.Execution) ? 'ring-2 ring-green-500' : ''
      } flex flex-col h-full`}>
        <div className="p-6 flex flex-col flex-1">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-xl font-semibold text-shopware-dark-blue flex-1">
              {project.title}
            </h3>
            {isUserProject && labDayStatus === 'submission' && (
              <button
                onClick={() => setShowEditModal(true)}
                className="ml-2 p-2 text-gray-500 hover:text-shopware-blue transition-colors"
                title="Edit your project"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <p className="text-gray-600 mb-4 line-clamp-3 flex-1">
            {project.description}
          </p>

          <div className="flex items-center text-gray-500 mb-4">
            <UserGroupIcon className="h-5 w-5 mr-2" />
            <div className="flex flex-wrap gap-2">
              {teamMembers.map((member, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-shopware-light-blue text-shopware-dark-blue"
                >
                  {member}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-auto flex justify-between items-end pt-4 gap-2">
            <div className="space-y-2">
              {renderNominateButton('Innovation')}
              {renderVoteButton('Innovation')}
            </div>
            <div className="space-y-2">
              {renderNominateButton('Execution')}
              {renderVoteButton('Execution')}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-shopware-dark-blue mb-4">
              Edit Project
            </h3>
            <form onSubmit={handleUpdateProject}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Members (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editForm.team_members}
                    onChange={(e) => setEditForm({ ...editForm, team_members: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                    required
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 bg-shopware-blue text-white py-2 rounded-xl hover:bg-shopware-dark-blue transition-all"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-xl hover:bg-gray-400 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectCard; 